import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Progress, Tag, message } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import CommonDialog from "../../../../global/dialog";
import { interceptor } from "../../../../../utils/interceptor";

const CHUNK_SIZE = 3;

const STATUS = {
  PENDING: "pending",
  CHECKING: "checking",
  PASS: "pass",
  FAIL: "fail",
};

const statusMeta = {
  [STATUS.PENDING]: { color: "default", icon: <ClockCircleOutlined />, label: "Pending" },
  [STATUS.CHECKING]: { color: "processing", icon: <LoadingOutlined />, label: "Checking" },
  [STATUS.PASS]: { color: "green", icon: <CheckCircleOutlined />, label: "Pass" },
  [STATUS.FAIL]: { color: "red", icon: <CloseCircleOutlined />, label: "Fail" },
};

const buildRows = (ips = []) =>
  ips.map((ip) => ({
    id: ip._id,
    host: ip.host,
    port: ip.port,
    status: STATUS.PENDING,
    message: "",
  }));

const EnhancedBulkHealthCheckStaticResidentialIp = ({
  open,
  setOpen,
  ips = [],
  onComplete,
}) => {
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const cancelRef = useRef(false);
  const ipsSnapshotRef = useRef([]);

  useEffect(() => {
    if (!open) {
      cancelRef.current = true;
      return;
    }
    cancelRef.current = false;
    ipsSnapshotRef.current = ips;
    setRows(buildRows(ips));
    setRunning(false);
    setFinished(false);
    // Snapshot only when dialog opens — avoid resetting mid-run when list refetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stats = useMemo(() => {
    const total = rows.length;
    const pass = rows.filter((r) => r.status === STATUS.PASS).length;
    const fail = rows.filter((r) => r.status === STATUS.FAIL).length;
    const checked = pass + fail;
    const percent = total ? Math.round((checked / total) * 100) : 0;
    return { total, pass, fail, checked, percent };
  }, [rows]);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const checkOne = async (row) => {
    updateRow(row.id, { status: STATUS.CHECKING, message: "" });
    try {
      const axiosInstance = interceptor();
      const res = await axiosInstance.post(`admin/proxies/${row.id}/health-check`);
      const payload = res?.data?.data || {};
      const passed = payload.isValid === true;
      const failMessage = payload.message || "Failed health check";

      updateRow(row.id, {
        status: passed ? STATUS.PASS : STATUS.FAIL,
        message: passed ? "Healthy" : failMessage,
      });
    } catch (err) {
      const errorMsg =
        err?.response?.data?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Health check failed";
      updateRow(row.id, { status: STATUS.FAIL, message: errorMsg });
    }
  };

  const runHealthChecks = async () => {
    const queue = buildRows(ipsSnapshotRef.current.length ? ipsSnapshotRef.current : ips);
    if (!queue.length) {
      message.warning("No IPs to check on the current page");
      return;
    }

    cancelRef.current = false;
    setRunning(true);
    setFinished(false);
    setRows(queue.map((row) => ({ ...row, status: STATUS.PENDING, message: "" })));

    for (let i = 0; i < queue.length; i += CHUNK_SIZE) {
      if (cancelRef.current) break;
      const chunk = queue.slice(i, i + CHUNK_SIZE);
      await Promise.all(chunk.map((row) => checkOne(row)));
      if (cancelRef.current) break;
    }

    setRunning(false);
    setFinished(true);
    if (!cancelRef.current) onComplete?.();
  };

  const handleClose = () => {
    cancelRef.current = true;
    setRunning(false);
    setOpen(false);
  };

  return (
    <CommonDialog
      open={open}
      setOpen={setOpen}
      title="Health Check"
      description={
        running
          ? `Checking ${stats.checked} of ${stats.total} IPs on the current page…`
          : finished
            ? `Done — ${stats.pass} passed, ${stats.fail} failed out of ${stats.total}.`
            : `Checks the ${stats.total} IP${stats.total === 1 ? "" : "s"} currently shown (filtered/search page), in batches of ${CHUNK_SIZE}.`
      }
      onCancel={handleClose}
      footer={[
        <Button
          key="close"
          className="h-10 min-w-[100px] hover:!border-primary hover:!text-primary"
          onClick={handleClose}
        >
          {running ? "Stop & Close" : "Close"}
        </Button>,
        <Button
          key="start"
          type="primary"
          className="!bg-primary text-white h-10 min-w-[130px]"
          onClick={runHealthChecks}
          disabled={running || !ips.length}
          loading={running}
        >
          {finished ? "Run Again" : running ? "Checking…" : "Start Check"}
        </Button>,
      ]}
    >
      <div className="space-y-4 pt-2">
        <div>
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>
              {stats.checked}/{stats.total} checked
            </span>
            <span>
              <span className="text-green-600 font-medium">{stats.pass} pass</span>
              {" · "}
              <span className="text-red-500 font-medium">{stats.fail} fail</span>
            </span>
          </div>
          <Progress
            percent={stats.percent}
            status={
              running ? "active" : stats.fail && finished ? "exception" : finished ? "success" : "normal"
            }
            showInfo={false}
            strokeColor="var(--primary-color, #1890ff)"
          />
        </div>

        <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100">
          {rows.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">No IPs on the current page.</div>
          ) : (
            rows.map((row) => {
              const meta = statusMeta[row.status] || statusMeta[STATUS.PENDING];
              return (
                <div
                  key={row.id}
                  className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {row.host}:{row.port}
                    </div>
                    {row.message ? (
                      <div className="text-xs text-gray-500 truncate mt-0.5" title={row.message}>
                        {row.message}
                      </div>
                    ) : null}
                  </div>
                  <Tag
                    color={meta.color}
                    icon={meta.icon}
                    className="m-0 capitalize flex items-center gap-1 flex-shrink-0"
                  >
                    {meta.label}
                  </Tag>
                </div>
              );
            })
          )}
        </div>
      </div>
    </CommonDialog>
  );
};

export default EnhancedBulkHealthCheckStaticResidentialIp;
